import { can } from '@misumi/shared'
import type { ReactNode } from 'react'
import { useId, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ShoppingCartOutlined, UploadOutlined } from '../../components/icons'
import { Button } from '../../components/ui/Button'
import { Card } from '../../components/ui/Card'
import { Icon } from '../../components/ui/Icon'
import { Spinner } from '../../components/ui/Spinner'
import { useToast } from '../../components/ui/ToastProvider'
import { apiClient } from '../../lib/api/client'
import { useRole, useSession } from '../../lib/auth/use-session'
import { displayNameFor } from '../../lib/fixtures/sourceable-lookup'
import { findSourceableById, toAddCartLineRequest } from '../cart/cart-catalog-lookup'
import { CART_ADD_BATCH_MAX_LINES } from '../cart/cart-limits'
import { addAmounts, formatMoney, multiplyAmount } from '../cart/cart-money'
import { AddPartsToRfqModal } from './AddPartsToRfqModal'
import { useShareHandoff } from './RfqCard'
import { RfqShareQuoteModal } from './RfqShareQuoteModal'
import { searchAddPartsCandidates } from './add-parts-search'
import {
  etaForRfq,
  formatQuantity,
  isQuoteLineAddable,
  resolveQuoteVariantId,
  shipByForQuote,
} from './domain'
import type { Quote, QuoteVariant, Rfq, RfqLineView } from './domain'
import { groupTitleForVariantId } from './region-groups'
import { buildAppendLinesRequest } from './review-domain'
import {
  ADD_PARTS_QUOTE_IN_PROGRESS_COPY,
  type AppendLinesConflict,
  classifyAppendConflict,
} from './rfq-append-lines'
import { previewDiscountedUnitPrice } from './rfq-volume-break-preview'
import { useRfqQuote } from './use-rfq-quote'
import { applyVariantRatio, deliveryOptionForVariant } from './variant-pricing'

export interface RfqDetailSummaryRailProps {
  readonly rfq: Rfq
  /** Re-fetches the RFQ after an action that changes it (Checkout landing
   * lines in the cart, Resubmit RFQ flipping this expired RFQ in place to
   * `quoting`, or UID-265b's post-submit line append) — mirrors `RfqCard`'s
   * `onChanged` contract.
   *
   * UID-265b/265d (ADR-0093 D3/D4) — WIDENED with one OPTIONAL argument: the
   * append (`POST /rfq/{rfqId}/lines`) and the resubmit
   * (`POST /rfq/{rfqId}/resubmit`) both answer `200` + the whole refreshed
   * `RfqView`, so the caller can hydrate from the response it already has
   * instead of paying for a second `GET`. Called with no argument by every
   * pre-existing caller/path, which keeps "just refetch" as the default. */
  readonly onChanged: (fresh?: Rfq) => void
  /** UID-238 item 2 (ADR-0056 amendment) — the pricing-region selection,
   * LIFTED to `RfqDetailPage` and shared with the co-mounted `RfqCard`'s
   * `QuotedLinesPanel` picker, so this rail's Region/Total stay in sync with
   * whatever the customer picked there instead of always showing the first
   * variant (the ADR-0056 Decision 2/D1 gap this closes). Only
   * `ReturnedSummaryRail` reads it. */
  readonly selectedVariantId?: QuoteVariant['id'] | undefined
  readonly onVariantSelect?: ((id: QuoteVariant['id']) => void) | undefined
}

/**
 * UID-135 (ADR-0056 Decision 5) — the sticky "Request summary" panel on the
 * `/rfq/:rfqId` detail page. NOT the compose-screen's `RequestSummaryRail`
 * (different concern/data source, see that file's doc comment) — this is a
 * new, status-adaptive rail composed from the already-fetched `Rfq` + its
 * own `Quote` fetch (via the shared `useRfqQuote` hook, UID-134/135).
 *
 * Three variants per the Gate 2 UX spec §3: Returned/Quoted (parts/region/
 * lead-time/total + Checkout + "Share quote" — UID-294 renamed it from
 * "Download quote" (ADR-0104 D3), UID-618 shortened it from
 * "Generate & share quote"), Active/Requested (parts list + a
 * non-interactive "Requested" pill), Expired (Resubmit RFQ). Its
 * primary actions delegate to the SAME underlying BFF flows (accept-then-cart
 * for Returned; resubmit-in-place for Expired, UID-264) — this rail is the
 * surface for those actions, never a second commit path (the UID-126
 * boundary).
 *
 * UID-238 item 2 (ADR-0056 amendment, closes the Decision 2/D1 gap noted
 * below historically): this rail and `RfqCard`'s own `QuotedLinesPanel` are
 * still separate component instances (each independently fetching via
 * `useRfqQuote`), but they no longer own independent, unsynced selection
 * state — `RfqDetailPage` lifts ONE `selectedVariantId` and passes it (plus
 * its setter) to both as controlled props. Checkout below now accepts the
 * quote (if not already) and adds every `ready` line at whichever variant is
 * CURRENTLY SELECTED (falling back to the first/default only when nothing
 * has been explicitly picked) — the same resolution `QuotedLinesPanel` uses,
 * so what the customer sees selected in either surface is what Checkout
 * actually submits. A 409 on an already-in-cart line is treated as
 * resolved, matching the panel's own convention.
 */
export function RfqDetailSummaryRail({
  rfq,
  onChanged,
  selectedVariantId,
}: RfqDetailSummaryRailProps) {
  const showsQuote = (rfq.status === 'quoted' || rfq.status === 'accepted') && Boolean(rfq.quoteId)
  const { quote, quoteLoadState } = useRfqQuote(rfq.id, showsQuote)

  if (rfq.status === 'quoted' || rfq.status === 'accepted') {
    return (
      <ReturnedSummaryRail
        rfq={rfq}
        quote={quote}
        quoteLoadState={quoteLoadState}
        onChanged={onChanged}
        selectedVariantId={selectedVariantId}
      />
    )
  }
  if (rfq.status === 'requested' || rfq.status === 'quoting') {
    // UID-265b (ADR-0093 D6, reversing ADR-0094 D7): `onChanged` is now
    // genuinely required here — the append mutates the RFQ.
    return <ActiveSummaryRail rfq={rfq} onChanged={onChanged} />
  }
  if (rfq.status === 'expired') {
    return <ExpiredSummaryRail rfq={rfq} onChanged={onChanged} />
  }
  // `draft`/`declined` — no rail variant is specified for these; the detail
  // page's `RfqCard` body already carries the decision note for `declined`.
  return null
}

function RailCard({ children }: { readonly children: ReactNode }) {
  return (
    <Card aria-labelledby="rfq-detail-summary-heading" className="rfq-detail-summary">
      {/* UID-203: design (`Fictiv Platform.dc.html` L4016) is
       * font-size:15px;font-weight:700 — down from `--font-size-heading`
       * (20px). Set inline (as this heading already was) rather than via a
       * new class, since it's this one element only. */}
      <h2 id="rfq-detail-summary-heading" style={{ fontSize: '15px', fontWeight: 700 }}>
        Request summary
      </h2>
      {children}
    </Card>
  )
}

/**
 * UID-327 AC4 (ADR-0112) — the shared `.rfq-detail-summary-field` `<dt>`/`<dd>`
 * pair, extracted so the Expired rail's new Parts/Quoted rows don't hand-roll
 * a fourth copy of the markup `ReturnedSummaryRail` already repeats for
 * Parts/Region/Lead time/etc. (`:375-501`). Takes a `ReactNode` for `dd` (not
 * just a string) so it still covers rows like Region's styled `<span>` or the
 * Quantity row's `<input>`, should a caller need that later — the Expired
 * rail's own two rows only ever pass plain text/`<time>`.
 *
 * `ReturnedSummaryRail`'s own rows are NOT rebuilt onto this helper: several
 * of them (Quantity's conditional `<label htmlFor>`, the qualifier `<span>`
 * inside Shipping's `<dt>`) don't reduce to a single `dt`/`dd` pair without
 * either widening this helper's props past what the Expired rail needs or
 * risking a rendered-DOM change the "keep every existing test green with no
 * assertion edits" bar in the ticket forbids verifying. Left as file-local,
 * non-shared risk instead of a broad, unverifiable refactor.
 */
function SummaryField({
  term,
  children,
}: {
  readonly term: ReactNode
  readonly children: ReactNode
}) {
  return (
    <div className="rfq-detail-summary-field">
      <dt>{term}</dt>
      <dd>{children}</dd>
    </div>
  )
}

/**
 * UID-401 W1 (Gate-5) — the honest qualifier for the Shipping row's `<dt>`. Before this
 * fix the qualifier was a HARDCODED "(ground)" — harmless pre-UID-401, when the amount was
 * a flat constant, but actively WRONG once the amount became method-derived: an
 * `overnight` RFQ would render its real $59.36 labelled "(ground)", a figure contradicting
 * its own label. Driven off `rfq.shipMethod` — the resolved method the server already
 * sends (`RfqView.shipMethod`, ADR-0053 §6) — rather than the `*Basis` discriminator, which
 * says WHY a figure exists/doesn't but not WHICH method. Returns `null` (no qualifier
 * rendered at all) for `own_account` and for "no method chosen" — this rail never shows an
 * `own_account` figure to qualify (see {@link shippingRowContent}), and there is no honest
 * label to invent when nothing was chosen.
 */
function shipMethodQualifier(shipMethod: Rfq['shipMethod'] | null | undefined): string | null {
  if (shipMethod === 'ground') return 'ground'
  if (shipMethod === 'overnight') return 'overnight'
  return null
}

/** What a money row's `<dd>` should render, plus whether it is a real figure (money,
 * default styling) or an explanatory note (secondary/`caption-text` styling) — see
 * {@link shippingRowContent}/{@link taxRowContent}. */
interface MoneyRowContent {
  readonly content: string
  readonly isFigure: boolean
}

/**
 * UID-401 W3 (Gate-5) — the Shipping row NEVER disappears. Before this fix the row was
 * `{rfq.quoteSummary.shipping && …}`, so whenever the server had no honest shipping figure
 * (no ship method chosen, or `own_account`) the row rendered NOTHING — no figure, no
 * explanation. An RFQ can reach `quoted` with no ship method selected (`rfqSubmitBlockers`
 * gates only parts/date/services, not shipping), so this was reachable, not theoretical.
 *
 * Prefers `rfq.costEstimate` for the WHY (`shippingBasis`/`shippingNote` — the server always
 * emits `costEstimate`, for every status, unlike the status-gated `quoteSummary`) and falls
 * back to `rfq.shipMethod` alone when an older/test-only fixture supplies `quoteSummary`
 * without `costEstimate` — so the row is never silently blank either way.
 *
 * Constraint (ADR-0100 D1, cited by the ticket for this rail too): never `0.00`, never `—`,
 * never a bare placeholder. `own_account` renders its note ("Billed by carrier…"), never a
 * figure — even the rate card's own `0.00` for that method, which would lie about freight
 * being free when the carrier is billing the customer directly.
 */
function shippingRowContent(
  rfq: Pick<Rfq, 'shipMethod' | 'quoteSummary' | 'costEstimate'>,
): MoneyRowContent & { readonly qualifier: string | null } {
  const shipping = rfq.quoteSummary?.shipping
  if (shipping) {
    return {
      content: formatMoney(shipping.amount, shipping.currency),
      isFigure: true,
      qualifier: shipMethodQualifier(rfq.shipMethod),
    }
  }
  const basis = rfq.costEstimate?.shippingBasis
  if (basis === 'billed_by_carrier' || rfq.shipMethod === 'own_account') {
    return {
      content: rfq.costEstimate?.shippingNote ?? 'Billed by carrier',
      isFigure: false,
      qualifier: null,
    }
  }
  if (basis === 'no_ship_method' || !rfq.shipMethod) {
    return { content: 'No shipping method selected yet', isFigure: false, qualifier: null }
  }
  // `nothing_priced`, or a `rate_card` basis whose amount was somehow still null — neither
  // is expected to reach a `quoted`/`accepted` RFQ (both require priced lines), but this
  // stays an honest, explicit fallback rather than a silently vanished row.
  return { content: 'Not available for this quote', isFigure: false, qualifier: null }
}

/** UID-401 W3 (Gate-5) — the Tax row's counterpart to {@link shippingRowContent}: never
 * disappears when the server has no destination rate to apply (no ship-to address
 * resolved). See that function's doc comment for the full rationale; the same
 * `costEstimate`-preferred / `quoteSummary`-only-fixture fallback shape applies. */
function taxRowContent(rfq: Pick<Rfq, 'quoteSummary' | 'costEstimate'>): MoneyRowContent {
  const tax = rfq.quoteSummary?.tax
  if (tax) {
    return { content: formatMoney(tax.amount, tax.currency), isFigure: true }
  }
  if (rfq.costEstimate?.taxBasis === 'no_destination') {
    return { content: 'No ship-to address selected', isFigure: false }
  }
  return { content: 'Not available for this quote', isFigure: false }
}

/** §3.1 — Returned/Quoted. */
function ReturnedSummaryRail({
  rfq,
  quote,
  quoteLoadState,
  onChanged,
  selectedVariantId,
}: {
  readonly rfq: Rfq
  readonly quote: Quote | undefined
  readonly quoteLoadState: 'loading' | 'loaded' | 'error'
  readonly onChanged: (fresh?: Rfq) => void
  readonly selectedVariantId: QuoteVariant['id'] | undefined
}) {
  const { showToast } = useToast()
  const navigate = useNavigate()
  const [checkingOut, setCheckingOut] = useState(false)
  // UID-366 (ADR-0119) — defence in depth for the quote-accept that Checkout
  // runs before it writes any cart line. A toast is the wrong (only) channel
  // for this: it self-dismisses, so a customer who looked away is left with an
  // unchanged screen and no idea the click did nothing. This holds a
  // PERSISTENT, `role="alert"` blocker next to the CTA until an attempt
  // succeeds. Independent of the server fix — the accept can still fail for
  // reasons the client cannot pre-empt (409, 5xx, network).
  const [checkoutBlocker, setCheckoutBlocker] = useState<string | null>(null)
  // UID-294 §4 (ADR-0104 D3) — the UID-54 Act-3 handoff, REWIRED to this
  // rail's single "Share quote" button (named "Generate & share quote" until
  // UID-618; was the parts panel's
  // standalone "Mark ready & share", `RfqCard.tsx`'s now-removed
  // `ShareHandoffAction`). Reuses the SAME exported `useShareHandoff` hook —
  // no new endpoint, no rebuilt flow, only the entry point moves.
  //
  // `enabled` mirrors `RfqCard.tsx`'s OWN gating exactly
  // (`rfq.status === 'accepted'`) rather than "true for this whole rail" —
  // this rail also renders for a merely-`quoted` (not yet accepted) RFQ, and
  // the background existence check has no reason to fire a
  // `GET /rfq/{rfqId}/session` for an RFQ that can never legally have a
  // session yet (`markReady`'s server precondition is `rfq.status ===
  // 'accepted'`, `session/service.ts`). The button still renders in the
  // `quoted` sub-state (matching the design, which does not distinguish
  // quoted vs. accepted here) — clicking it before Checkout/accept still
  // reaches `markReady`, which surfaces the server's honest "no accepted
  // quote" conflict detail rather than a silent no-op.
  const handoff = useShareHandoff(rfq.id, rfq.status === 'accepted')
  // UID-325 (F4) — gate the Checkout CTA on the SAME capability
  // `CheckoutPage` itself enforces (`can(role, 'order:submit')`, its
  // `isAuthorized`). Before this, an Engineer saw a fully enabled primary
  // CTA that navigated to `/checkout` and landed on "Checkout isn't
  // available for your role — Placing an order is available to Purchasers
  // only", after the click had already pushed the RFQ's lines into the cart.
  // The RBAC rule is correct; presenting an enabled CTA for it was not.
  //
  // Disabled with a visible explanation rather than hidden: the customer can
  // see that checkout exists and why they can't complete it (so they know to
  // hand off to a Purchaser), which a vanished button doesn't tell them. The
  // reason is also wired to the button via `aria-describedby`, so it reaches
  // a screen-reader user who never sees the note's position.
  //
  // NOTE: an Engineer currently sees this CTA ENABLED, not disabled — a
  // DEMO-ONLY override (`packages/shared/src/authz.ts` `ROLE_CAPABILITIES.
  // engineer`) grants `order:submit` too. That is a deliberate, scoped
  // relaxation for demo/eval purposes, not the pre-UID-325 defect described
  // above returning; the gating logic here is unchanged — it still asks
  // `can(role, 'order:submit')` and would correctly disable/explain again the
  // moment that demo-only grant is reverted.
  const role = useRole()
  const canCheckout = role !== null && can(role, 'order:submit')
  const checkoutBlockedNoteId = useId()

  /** UID-438 (ADR-0143 D6) — ONE path for every handoff sub-state. The
   * mark-ready POST moved INTO the modal (it is what its "Generate quote"
   * button does, and what its generating state waits on), so this button no
   * longer branches on `subState` at all: it just opens the modal at its
   * confirm step.
   *
   * That is safe for all three sub-states because `mark-ready` is idempotent
   * for a live session (re-marking returns the same one, never a duplicate —
   * `rfq.yaml`'s `markRfqReady`) and mints a fresh one for an expired session,
   * which is exactly what the retired modal's separate "Create new link"
   * branch used to call. Consequence, accepted: a user who already generated a
   * link sees the confirm step again rather than skipping to it. The design has
   * no already-shared shortcut, so that matches it. */
  function handleGenerateShare() {
    handoff.openModal()
  }

  if (quoteLoadState === 'error') {
    // §5's "Quote sub-fetch error" row: the rest of the detail page still
    // renders (parts/tracker/etc. read from the RFQ itself), so this rail
    // degrades to an inline error in place of Total/Checkout only.
    return (
      <RailCard>
        <p role="alert">Couldn&rsquo;t load this quote. Try refreshing the page.</p>
      </RailCard>
    )
  }
  if (quoteLoadState === 'loading' || !quote) {
    // UID-137 §2.5 (ADR-0060 §1/§2): `fxspin`, reusing the existing
    // `.spinner`/`Spinner` — same fix as `RfqCard.tsx`'s identical loading
    // line, no new keyframe.
    return (
      <RailCard>
        <p className="rfq-card-loading">
          <Spinner size="sm" aria-hidden="true" /> Loading your quote…
        </p>
      </RailCard>
    )
  }

  // code-review fix: `readyLines` must be the EXACT set of lines Checkout's
  // batch call below will send — a `ready` + priced line with no
  // `deliveryPromise` was previously counted into Total/enable here but
  // silently excluded by the batch body's own `line.deliveryPromise` guard
  // (originally a per-line add-loop's `continue`, since replaced by
  // UID-614's `flatMap`), so the Total and the enabled button both
  // overstated what Checkout would actually add (a success toast reading
  // "Added M", M < N, with no explanation). Single source of truth: this
  // filter feeds Total, Quantity, the button's `disabled`, AND the batch
  // body below.
  //
  // UID-302 fix: now reuses the shared, exported `isQuoteLineAddable`
  // (`domain.ts`) instead of this inlined copy of the same three-clause
  // predicate — identical logic, consolidated so `RfqCard.tsx`'s
  // `isRowAddable` and `subtotalForQuote` can't drift from this again.
  const readyLines = quote.lines.filter(isQuoteLineAddable)
  // Review-round fix (reported after UID-611 shipped) — same ADR-0131
  // principle as `RfqCard.tsx`'s `quantityBySourceableId`: the EFFECTIVE
  // per-line quantity is the RFQ REQUEST line's quantity (`rfq.lines`, kept
  // live by `PATCH /rfq/{rfqId}/lines/{lineId}`), NOT the Quote `LineItem`'s
  // copy, which is a point-in-time snapshot taken when the quote was priced
  // and never updated by a later quantity edit. Every `readyLines`-derived
  // quantity/price below (Total, Quantity display, and the checkout batch
  // body) must resolve through this map, or a quantity edit on an already-
  // quoted RFQ silently has no visible effect anywhere on this rail.
  const quantityBySourceableId = new Map(
    (rfq.lines as readonly RfqLineView[]).map((line) => [line.sourceable.id, line.quantity]),
  )
  const liveQuantity = (line: Quote['lines'][number]) =>
    quantityBySourceableId.get(line.sourceable.id) ?? line.quantity
  // UID-238 item 2: resolve against the LIFTED selection first, falling
  // back to the first variant only when nothing has been explicitly picked
  // yet — the same `selectedVariantId ?? variants[0]?.id` resolution
  // `QuotedLinesPanel` (RfqCard.tsx) uses, so this rail's Region/Total/lead
  // time and Checkout's actual submission always agree with whichever
  // variant the customer sees selected in either surface.
  // UID-325 (Gate 5) — via the shared `domain.ts` resolver rather than a third
  // inline copy of the fallback, so this rail and the two RfqCard surfaces
  // cannot resolve to different variants (which would show one region's price
  // and submit another's).
  const variants = quote.variants
  const variant = variants?.find((v) => v.id === resolveQuoteVariantId(quote, selectedVariantId))
  // UID-611 (ADR-0179 D5) — the demo-only, client-side editable quantity
  // (ADR-0094 D5) is RETIRED: it never persisted, never called a repricing
  // endpoint, and is superseded by the real, persisted quantity input now on
  // every RFQ line (`RfqPartsCard.tsx`'s `RfqLineQtyInput`, wired via
  // `RfqCard`/`QuotedLinesPanel`). `singleReadyLine` still identifies the
  // single-part case this rail's Quantity/lead-time display is scoped to
  // (matching the design's `showQtyStepper:isReturned && !isMultiPart`
  // gating, `dc.html:9882`); `effectiveQty` is now simply the REAL server
  // quantity, with no local override.
  const singleReadyLine = readyLines.length === 1 ? readyLines[0] : undefined
  const effectiveQty = singleReadyLine ? liveQuantity(singleReadyLine).value : 0
  // TEMPORARY (demo reversal, revertable) — `previewDiscountedUnitPrice`
  // applies the same volume-break curve the backend's `rfq-cost-estimate.ts`
  // now applies (see that file's doc comment), against each line's LIVE
  // quantity, BEFORE the region-variant ratio scaling below (an orthogonal,
  // frontend-only display scale) — so this Total agrees with the per-line
  // Unit price cell (`RfqCard.tsx`), which discounts in the same order. See
  // `rfq-volume-break-preview.ts`'s own doc comment for the one-line revert.
  const total =
    readyLines.length > 0
      ? {
          amount: singleReadyLine
            ? multiplyAmount(
                variant
                  ? applyVariantRatio(
                      previewDiscountedUnitPrice(
                        singleReadyLine.price?.amount ?? '0',
                        effectiveQty,
                      ),
                      variant.ratio,
                    )
                  : previewDiscountedUnitPrice(singleReadyLine.price?.amount ?? '0', effectiveQty),
                effectiveQty,
              )
            : addAmounts(
                readyLines.map((line) => {
                  const qty = liveQuantity(line).value
                  const discountedUnit = previewDiscountedUnitPrice(line.price?.amount ?? '0', qty)
                  return multiplyAmount(
                    variant ? applyVariantRatio(discountedUnit, variant.ratio) : discountedUnit,
                    qty,
                  )
                }),
              ),
          currency: readyLines[0]?.price?.currency ?? 'USD',
        }
      : undefined
  const shipBy = variant?.leadTimeLabel ?? shipByForQuote(quote) ?? 'N/A'
  // UID-401 W1/W3 (Gate-5) — computed once here (not inline in the JSX below)
  // so both the qualifier and the no-figure fallback text derive from the
  // same read of `rfq`, and so the itemized-breakdown JSX can stay a plain
  // render of these two results.
  const shippingRow = shippingRowContent(rfq)
  const taxRow = taxRowContent(rfq)

  // UID-424 — same lookup as `RfqCard.tsx`'s `partBySourceableId`: the
  // per-line DISPLAY identity (name/partNumber) lives on the RFQ's OWN lines
  // (`RfqLineView.part`), not on the Quote's `LineItem`. Used below as the
  // fallback label the bulk "accept & checkout" cart-add carries when the
  // resolved fixture Sourceable itself has no name/sku (`findSourceableById`
  // miss on a real, non-fixture part).
  const partBySourceableId = new Map<string, RfqLineView['part']>(
    (rfq.lines as readonly RfqLineView[]).map((line) => [line.sourceable.id, line.part]),
  )

  async function handleCheckout() {
    if (!quote) return
    // UID-614 (S2) — one batch call instead of N serialized POSTs (71
    // requests → 3 for a 69-line RFQ). `flatMap` (not `map`) so the
    // `!line.price || !line.deliveryPromise` guard still yields `[]`,
    // exactly like the old loop's `continue`. Iterates `readyLines` (not
    // `quote.lines` re-filtered) — the exact same set the Total/Quantity/
    // button-enable above are computed from, so nothing eligible-by-that-
    // count is silently skipped here. Belt-and-braces: `readyLines` is
    // already `isQuoteLineAddable`-filtered (price + deliveryPromise both
    // present), so this guard is currently unreachable — kept in case that
    // filter's contract ever loosens. Built BEFORE the accept below (not
    // just before the POST) so the &gt;500-line guard checks the ACTUAL batch
    // body, not a count assumed equal to it.
    const lines = readyLines.flatMap((line) => {
      if (!line.price || !line.deliveryPromise) return []
      const price = variant
        ? { ...line.price, amount: applyVariantRatio(line.price.amount, variant.ratio) }
        : line.price
      // `deliveryPromise` is ALWAYS the line's own — untouched, unconditionally,
      // regardless of variant selection (ADR-0179 R1, mirrors RfqCard.tsx's
      // `effectiveLine`). A variant is a regional pricing adjustment, not a
      // replacement lead time; collapsing every line onto
      // `committedDateForVariant(...)` destroyed the per-kind distinction the
      // backend now computes (auto-quote-policy.ts / cpq-writeback.ts) and made
      // UID-617's checkout delivery-plan step structurally unreachable.
      const deliveryPromise = line.deliveryPromise
      return [
        toAddCartLineRequest(
          // Resolve the bare SourceableRef to its full fixture Sourceable
          // (name + catalogSku) so the human part labels are sent — otherwise
          // the cart renders the raw sourceable UUID (UID-212 bug).
          //
          // UID-424: falls back to the RFQ line's OWN `part.name`/
          // `part.partNumber` (`partBySourceableId`, above) when
          // `findSourceableById` misses for a real part the demo fixture
          // catalog doesn't know about — only wins when the resolved
          // Sourceable itself carries no label, so a catalog part is
          // unaffected.
          findSourceableById(line.sourceable.id) ?? line.sourceable,
          // ADR-0131, updated by the `liveQuantity` fix above: send the
          // LIVE `rfq.lines` quantity (the real, persisted, possibly-edited
          // request quantity), not the Quote line's frozen snapshot — same
          // reasoning as `effectiveQty`/Total above, now applying to every
          // line, not just the single-part case (which used to be the only
          // one with an "effective" override).
          liveQuantity(line),
          {
            price,
            deliveryPromise,
            sourceQuoteId: quote.id,
            sourceRfqId: rfq.id,
          },
          {
            name: partBySourceableId.get(line.sourceable.id)?.name ?? undefined,
            partNumber: partBySourceableId.get(line.sourceable.id)?.partNumber ?? undefined,
          },
        ),
      ]
    })
    // code-review fix — checked BEFORE the irreversible quote/accept below:
    // an over-cap RFQ must never accept a quote it then has no way to add to
    // the cart, leaving the buyer stranded with an accepted quote and no
    // recourse.
    if (lines.length > CART_ADD_BATCH_MAX_LINES) {
      const blocker = `This RFQ has ${lines.length} priced lines, over the ${CART_ADD_BATCH_MAX_LINES}-line checkout limit. Contact support to complete this order.`
      setCheckoutBlocker(blocker)
      showToast(blocker, 'error')
      return
    }
    setCheckingOut(true)
    try {
      let accepted = quote.state === 'accepted'
      if (!accepted) {
        const acceptRes = await apiClient.POST('/rfq/{rfqId}/quote/accept', {
          params: { path: { rfqId: rfq.id } },
        })
        if (!acceptRes.data) {
          // UID-366 (ADR-0119) — a 403 here means the caller's role may not
          // accept this quote, so the server's neutral "You do not have
          // permission to do that" is true and useless: it names no way
          // forward. Substitute the ONE actionable instruction. Any other
          // failure keeps the server detail (it carries the real reason —
          // 409 already-decided, stale quote, 5xx).
          const blocker =
            acceptRes.error?.status === 403
              ? 'This quote must be accepted before checkout. Ask a teammate with RFQ permissions to accept it, then try again.'
              : (acceptRes.error?.detail ?? 'Could not accept this quote. Please try again.')
          setCheckoutBlocker(blocker)
          showToast(blocker, 'error')
          // The early return is load-bearing: no cart line is written and no
          // navigation happens off a failed accept.
          return
        }
        accepted = true
      }
      // Cleared only on a genuinely successful accept (or an already-accepted
      // quote), so a stale blocker never outlives the condition it describes.
      setCheckoutBlocker(null)
      // code-review fix: `add-batch`'s duplicate handling is UPSERT (replace, UID-615
      // S3 — was SKIP under S1), so a 2xx `CartView` response reports the WHOLE cart
      // (every pre-existing line plus every added-or-replaced one) — it carries no
      // added-vs-replaced marker. A count derived from it (e.g. "requested ids present
      // in the response") is algebraically just `lines.length` restated, and would go
      // on lying — a re-checkout of the SAME already-accepted quote (e.g. after
      // pressing Back) replaces every line with itself yet the response would look
      // identical to a fresh 69-line add. No truthful per-line count is derivable from
      // this endpoint's response, so the toast makes no count claim.
      const { data, error } = await apiClient.POST('/cart/lines/add-batch', { body: { lines } })
      // The batch is all-or-nothing (UID-403) — there is no "some failed,
      // some didn't" to report, unlike the old per-line loop's `anyFailed`.
      const failed = !data
      if (!failed) {
        // ADR-0131 A2 amendment — carry the ACCEPTED pricing variant into the cart's delivery
        // option so the shipping SOURCE (and rate) flows through the existing delivery-option path
        // identically on the Cart page and Checkout. The variant is RFQ-level, so ONE PUT for the
        // whole checkout. Best-effort: a failure here must not strand the buyer (lines are added),
        // so it never flips `failed` — checkout still proceeds.
        const deliveryOptionId = deliveryOptionForVariant(variant?.id)
        if (deliveryOptionId !== undefined) {
          await apiClient.PUT('/cart/delivery-option', { body: { deliveryOptionId } })
        }
        showToast('Added to your cart.', 'success')
        onChanged()
        // UID-238 item 1 (Fictiv Platform.dc.html:7329-7358): Checkout builds
        // the cart lines AND navigates to the real /checkout route in the
        // same click — this rail used to stop after the cart-add, leaving
        // the customer stranded with no way forward.
        navigate('/checkout')
      } else {
        // The batch is all-or-nothing, so a failure here means NOTHING was
        // committed — the server's `detail` is the real reason (422
        // priced-only, 403 capability/CSRF, 5xx), unlike the old per-line
        // loop's generic "some lines" message.
        const blocker = error?.detail ?? 'Could not add these lines to your cart. Please try again.'
        setCheckoutBlocker(blocker)
        showToast(blocker, 'error')
      }
    } finally {
      setCheckingOut(false)
    }
  }

  return (
    <RailCard>
      <dl className="rfq-detail-summary-fields">
        <div className="rfq-detail-summary-field">
          <dt>Parts</dt>
          {/* ADR-0115 F9 — a BARE integer, matching the design's literal
           * `{{ r.partCount }}` (`dc.html:3700`, no runtime pluralization —
           * unlike `RfqPartsCard`'s "N part(s)" header, which IS
           * design-literal pluralization-free text for a DIFFERENT surface;
           * don't conflate the two). */}
          <dd>{readyLines.length}</dd>
        </div>
        {variant && (
          <div className="rfq-detail-summary-field">
            <dt>Region</dt>
            {/* UID-440 (PHASE 1) — shows the REGION GROUP TITLE ("North
             * America" / "Overseas", `./region-groups`), the same
             * user-facing vocabulary `QuoteVariantSelector`'s column headers
             * use, rather than the raw per-variant `variant.region` string
             * (e.g. "US region") this row rendered before. Falls back to
             * `variant.region` when the resolver returns `undefined` (an id
             * that matches no known group) so the row never blanks.
             *
             * UID-294 §5 (ADR-0104 D4/D5, `Fictiv Platform.dc.html:3702`) —
             * design-literal navy BOLD TEXT (`--color-brand-primary`), never
             * a fabricated navigational link — the design markup is a plain
             * `<span>` with no `onClick`, so this stays inert. */}
            <dd className="rfq-detail-summary-region-value">
              {groupTitleForVariantId(variant.id) ?? variant.region}
            </dd>
          </div>
        )}
        <div className="rfq-detail-summary-field">
          <dt>Lead time</dt>
          <dd>{shipBy}</dd>
        </div>
        {/* UID-611 (ADR-0179 D5) — the single-part editable-quantity row
         * (ADR-0094 D5) is REMOVED from this rail: it was client-only,
         * non-persisted, and never called a repricing endpoint. The real
         * quantity input now lives on every RFQ line in "Parts in this
         * request" (`RfqPartsCard.tsx`'s `RfqLineQtyInput`), for every
         * status — see that component and `RfqCard.tsx`'s per-line PATCH
         * wiring. This rail keeps no Quantity row of its own any more. */}
        {/* UID-289 (ADR-0093 §C6) — the itemized cost breakdown when the RFQ
         * carries a `quoteSummary` (present-only-when quoted/accepted).
         * `partProduction` here is the CLIENT-derived `total` above (it
         * already re-applies the selected variant ratio and the demo-only
         * editable quantity, ADR-0093's "Variant interaction" note — one
         * display source per line so the numbers never disagree), NOT the
         * server's base-variant `quoteSummary.partProduction` figure.
         *
         * UID-401 (ADR-0125 D5) — CORRECTED. `shipping`/`tax` are NO LONGER
         * demo-fixture placeholders; they are REAL server-derived figures
         * (rate-card shipping keyed on the RFQ's persisted `shipMethod`,
         * destination sales tax off the resolved ship-to's
         * `salesTaxRatePct`) — see `rfq-cost-estimate.ts`. Still advisory
         * (`provenance: 'estimated'`), never order-eligible.
         *
         * UID-401 W2 (Gate-5, investigated — see ADR-0125 D5 amendment /
         * this ticket's report): the grand Total is STILL a client-side sum
         * of the three rows above, NOT `rfq.quoteSummary.total` /
         * `rfq.costEstimate.total` verbatim. The server's total is computed
         * against the BASE-VARIANT partProduction; the Part-production row
         * above is the variant-/qty-adjusted client figure (the whole point
         * of the Region picker on this screen). Reading the server total
         * verbatim would make Total silently disagree with the very
         * Part-production row printed one line above it whenever a non-base
         * variant is selected or the demo qty edit is used — the same kind
         * of self-contradicting display W1 exists to remove, just moved to
         * a different row. Summing the (already server-sourced) shipping/tax
         * addends against the correctly-scoped client partProduction keeps
         * the four rows FOOTING correctly, which a verbatim server total
         * cannot guarantee here. Falls back to the single Total row
         * (unchanged) when the RFQ carries no `quoteSummary`. */}
        {rfq.quoteSummary ? (
          <>
            {/* UID-485 b4 — the design rules OFF the cost breakdown from the
             * request rows above it (`dc.html:3705`) and rules Total off from
             * Tax (`dc.html:3709`). Both hairlines are rendered as a
             * `border-top` on the row BELOW them, not as `<hr>` elements: a
             * `<dl>`'s content model admits only `<dt>`/`<dd>` groups or
             * `<div>`s, so an `<hr>` between these rows would be invalid
             * markup (which is why this rail's ONE existing `<hr>`, in
             * `ExpiredSummaryRail`, sits OUTSIDE the `<dl>`). It also keeps
             * these purely decorative rules from announcing as `separator`
             * roles mid-list. See the two `--rule` modifiers in rfq.css for
             * the flex-gap spacing math. */}
            <div className="rfq-detail-summary-field rfq-detail-summary-field--rule">
              <dt>Part production</dt>
              <dd>{total ? formatMoney(total.amount, total.currency) : '—'}</dd>
            </div>
            {/* UID-401 W1/W3 (Gate-5) — the row is now ALWAYS rendered (never
             * `{shipping && …}`, which used to make it vanish with no
             * explanation whenever the server had no honest figure), and its
             * "(ground)"/"(overnight)" qualifier is now driven off the RFQ's
             * actual persisted `shipMethod` rather than a hardcoded literal —
             * see `shippingRowContent`/`shipMethodQualifier` above. */}
            <div className="rfq-detail-summary-field">
              <dt>
                Shipping
                {shippingRow.qualifier && (
                  <>
                    {' '}
                    <span className="rfq-detail-summary-qualifier">({shippingRow.qualifier})</span>
                  </>
                )}
              </dt>
              <dd className={shippingRow.isFigure ? undefined : 'caption-text'}>
                {shippingRow.content}
              </dd>
            </div>
            <div className="rfq-detail-summary-field">
              <dt>Tax</dt>
              <dd className={taxRow.isFigure ? undefined : 'caption-text'}>{taxRow.content}</dd>
            </div>
            <div className="rfq-detail-summary-field rfq-detail-summary-total rfq-detail-summary-field--rule-total">
              <dt>Total</dt>
              <dd>
                {total
                  ? formatMoney(
                      addAmounts([
                        total.amount,
                        rfq.quoteSummary.shipping?.amount ?? '0.00',
                        rfq.quoteSummary.tax?.amount ?? '0.00',
                      ]),
                      total.currency,
                    )
                  : '—'}
              </dd>
            </div>
          </>
        ) : (
          <div className="rfq-detail-summary-field rfq-detail-summary-total">
            <dt>Total</dt>
            <dd>{total ? formatMoney(total.amount, total.currency) : '—'}</dd>
          </div>
        )}
      </dl>
      <Button
        variant="primary"
        size="lg"
        className="rfq-checkout-btn"
        onClick={handleCheckout}
        loading={checkingOut}
        disabled={readyLines.length === 0 || !canCheckout}
        aria-describedby={canCheckout ? undefined : checkoutBlockedNoteId}
      >
        <ShoppingCartOutlined aria-hidden="true" /> Checkout
      </Button>
      {/* UID-325 (F4) — the explanatory affordance that makes the disabled
       * state above legible. Not an `aria-live` region: this is a static
       * property of the role, present from first render, not an event. */}
      {!canCheckout && (
        <p id={checkoutBlockedNoteId} className="caption-text rfq-checkout-blocked-note">
          Checking out is available to Purchasers — ask a Purchaser on your account to place this
          order.
        </p>
      )}
      {/* UID-366 (ADR-0119) — the PERSISTENT half of the accept-failure
       * disclosure. `role="alert"`
       * (not `aria-live="polite"`): this IS an event, it blocks the action the
       * customer just asked for, and unlike `.rfq-checkout-blocked-note` above
       * it is a failure, so it takes the error token. */}
      {checkoutBlocker && (
        <p role="alert" className="rfq-checkout-error">
          {checkoutBlocker}
        </p>
      )}
      {/* UID-294 §4 (ADR-0104 D3, `Fictiv Platform.dc.html:3712`) — renamed
       * from "Download quote" (whose honest demo-PDF toast is SUPERSEDED by
       * this real share entry point) to "Generate & share quote", the single
       * control that reaches the UID-54 mark-ready flow — the same flow
       * `RfqCard.tsx`'s now-removed standalone "Mark ready & share" button
       * used, just reached from here.
       *
       * UID-618 — the label is now just "Share quote". The generate step is
       * unchanged and still implicit in the click (see `handleGenerateShare`
       * -> `markReady` -> POST /rfq/{id}/mark-ready); only the wording drops
       * it, since the modal this opens is already titled "Share quote"
       * (`Fictiv Platform.dc.html:9158`) and naming the mechanism twice read
       * as two steps. This is a DELIBERATE deviation from dc.html:3712, which
       * still specifies "Generate & share quote".
       *
       * `rfq-generate-share-btn` is deliberately NOT renamed with the label:
       * it is a styling hook describing the flow (which still generates), and
       * renaming it would be a breaking change across rfq.css for no user-
       * visible gain.
       *
       * UID-438 (ADR-0143 D1) AMENDS what it opens: ADR-0104 D3's choice of
       * `ShareHandoffModal` is reversed in favour of the design's RFQ-variant
       * quote modal, which `dc.html:9775` shows this control opening in place.
       * The button itself — name, position, gating, endpoint, capability —
       * is exactly as D3 left it. */}
      {/* UID-438 (ADR-0143 D6) — no `loading` prop any more: the in-flight
       * mark-ready is now shown by the modal's own generating state, which is
       * already on screen when the POST fires. Leaving it here would spin a
       * button hidden behind the modal's backdrop. */}
      <Button
        variant="outline"
        size="lg"
        className="rfq-generate-share-btn"
        onClick={handleGenerateShare}
      >
        <UploadOutlined aria-hidden="true" /> Share quote
      </Button>
      {/* UID-438 (ADR-0143 D1), CORRECTED at Gate 5 — gated on the modal being
       * CLOSED rather than removed outright.
       *
       * While the modal is OPEN this must not render: `markError` is set from
       * inside the modal, which owns the disclosure over an intact confirm
       * step, so a second `role="alert"` here would sit behind the backdrop
       * (invisible) and still be announced — double-announcing one failure.
       *
       * But it must not vanish either. Escape during the generating state
       * closes the modal while the POST is still in flight; when that POST
       * then fails there is no modal left to carry the error, and a
       * self-dismissing toast is exactly the channel UID-366 rejected for this
       * class of failure. The gate restores the persistent half of the
       * disclosure for that path without reintroducing the double-announce. */}
      {!handoff.modalOpen && handoff.markError && (
        <p role="alert" className="rfq-generate-share-error">
          {handoff.markError}
        </p>
      )}
      {/* UID-438 (ADR-0143 D1/D3) — the design's RFQ-variant quote modal
       * (`dc.html:9775` -> `:7988` -> `:4962-5075`) in place of the unstyled
       * `ShareHandoffModal` (now retired). Every money figure is THIS rail's
       * own — `total`, `shippingRow`, `taxRow` and the same summed total the
       * `<dl>` above prints — passed in rather than re-derived, so the modal
       * physically cannot disagree with the rail behind it (ADR-0131), and
       * ADR-0125 W1/W3's honest non-figure fallbacks reach it verbatim. */}
      <RfqShareQuoteModal
        open={handoff.modalOpen}
        onClose={handoff.closeModal}
        rfq={rfq}
        partProduction={total}
        shippingRow={shippingRow}
        taxRow={taxRow}
        quoteTotal={
          total
            ? {
                amount: addAmounts([
                  total.amount,
                  rfq.quoteSummary?.shipping?.amount ?? '0.00',
                  rfq.quoteSummary?.tax?.amount ?? '0.00',
                ]),
                currency: total.currency,
              }
            : undefined
        }
        partCount={readyLines.length}
        arrivalLabel={shipBy}
        onGenerate={handoff.markReady}
        generateError={handoff.markError}
      />
    </RailCard>
  )
}

/**
 * UID-265b (ADR-0093 D6) — the status-AND-capability-derived render predicate
 * for the rail's one active-state control slot. Pure, so the matrix is
 * assertable without mounting.
 *
 * There is deliberately NO timer and NO `submittedAt`-within-N heuristic: the
 * prototype's 2200 ms `setTimeout` (`Fictiv Platform.dc.html` L8075-8078) was
 * a hand-rolled stand-in for the REAL `requested → quoting` beat
 * (`DEFAULT_AUTO_QUOTE_DELAY_MS = 2500`, `auto-quote-runner.ts:59`). The
 * status IS the signal, it is persisted, and a client-side window would drift
 * against an env-overridable server constant.
 *
 * Note `resubmitTransition` lands an RFQ DIRECTLY in `quoting` (ADR-0056), so
 * after a resubmit the user sees Edit RFQ immediately and never the pill. That
 * is the correct, intended consequence of a status-derived rule — it is the
 * inverse of the prototype's `rfqEditReady` map, which has no counterpart in
 * this state machine and must not be ported (ADR-0093 D6 (b)).
 */
export function activeRailControl(
  rfq: {
    readonly status: Rfq['status']
    // Explicitly `| undefined` rather than `Pick<Rfq, 'quotingSubState'>`: the
    // contract marks this present-only-when-meaningful, and the repo compiles
    // with `exactOptionalPropertyTypes`, under which an optional property is
    // not the same type as one that may be `undefined`.
    readonly quotingSubState?: Rfq['quotingSubState'] | undefined
  },
  canSubmitRfq: boolean,
): 'requested-indicator' | 'edit-rfq' | 'none' {
  if (rfq.status === 'requested') return 'requested-indicator'
  if (rfq.status !== 'quoting') return 'none'
  // ADR-0093 D1: `quoting` + `manual_takeover` is an explicit REFUSAL server-
  // side (`quote-in-progress`) — a human is pricing it. Offering the control
  // there is offering a guaranteed 409, so the refusal is mirrored in the
  // predicate. This is also what makes "hide the control on refetch" real
  // after that 409 lands.
  if (rfq.quotingSubState === 'manual_takeover') return 'none'
  // ADR-0093 D2: the capability gate is PART OF the predicate, not just the
  // status. `POST /rfq/{rfqId}/lines` requires `rfq:submit`, so a Purchaser
  // (who may READ an active RFQ under `account:read`) would otherwise see a
  // button that is a guaranteed 403.
  return canSubmitRfq ? 'edit-rfq' : 'none'
}

/** §3.2 — Active/Requested. */
function ActiveSummaryRail({
  rfq,
  onChanged,
}: {
  readonly rfq: Rfq
  readonly onChanged: (fresh?: Rfq) => void
}) {
  const { showToast } = useToast()
  const navigate = useNavigate()
  const role = useRole()
  const session = useSession()
  const [addPartsOpen, setAddPartsOpen] = useState(false)
  const [adding, setAdding] = useState(false)
  /** Guards the single `stale` retry (ADR-0093 D1's 409 (4)) so a persistently
   * losing CAS can never become an unbounded retry loop.
   *
   * Gate-5 L1 — the budget is ONE retry per user-initiated add, not one per
   * component lifetime. It was previously cleared only on success, so a single
   * exhausted retry left every later append in this rail's lifetime with zero
   * retries. Cleared at the start of each attempt (`handleAdd`) AND on modal
   * open/close, so neither a long-lived detail page nor a reopened dialog
   * inherits a spent budget. */
  const retriedStale = useRef(false)
  /** Gate-5 L2 — the double-submit guard. Deliberately a ref, NOT the `adding`
   * state: `if (adding) return` reads the render closure, so two invocations in
   * one tick both saw `false`. `adding` state stays, but only for the rendered
   * `disabled`/busy affordance. */
  const addingRef = useRef(false)

  const control = activeRailControl(rfq, role !== null && can(role, 'rfq:submit'))
  const alreadyOnRfq = new Set(rfq.lines.map((line) => line.sourceable.id))

  async function append(sourceable: Parameters<typeof buildAppendLinesRequest>[0][number]) {
    if (!session) return
    // Send ONLY the part being added — never the RFQ's existing set. See
    // `buildAppendLinesRequest`'s doc comment (ADR-0093 D2/F7).
    const body = buildAppendLinesRequest([sourceable], session.accountContext.accountId)
    const { data, error } = await apiClient.POST('/rfq/{rfqId}/lines', {
      params: { path: { rfqId: rfq.id } },
      body,
    })
    if (data) {
      // The 200 means "the part is on the RFQ and re-pricing has been kicked",
      // NEVER "repriced" (ADR-0093 D3). So: no optimistic total, and the copy
      // says re-pricing is under way.
      showToast('Part added — we’re re-pricing this RFQ.', 'success')
      // Hydrate straight from the response: it IS the whole refreshed RfqView.
      onChanged(data)
      return
    }
    const conflict: AppendLinesConflict =
      (error as { status?: number } | undefined)?.status === 409
        ? classifyAppendConflict(error?.detail)
        : 'unknown'
    switch (conflict) {
      case 'line-already-present':
        // Refresh so the modal's candidate list re-derives from the RFQ's real
        // line set (the part then renders as already-added, i.e. is excluded).
        // Explicitly NOT retried.
        showToast(error?.detail ?? 'That part is already on this RFQ.', 'error')
        onChanged()
        return
      case 'quote-in-progress':
        // A human is pricing it. Close the modal and refetch — the refreshed
        // `quotingSubState: manual_takeover` makes `activeRailControl` hide the
        // control entirely.
        showToast(ADD_PARTS_QUOTE_IN_PROGRESS_COPY, 'error')
        setAddPartsDialog(false)
        onChanged()
        return
      case 'lines-not-appendable':
        // The status advanced under the user. Refetch and let the rail
        // re-render into whichever variant now applies.
        showToast(error?.detail ?? 'Parts can no longer be added to this RFQ.', 'error')
        setAddPartsDialog(false)
        onChanged()
        return
      case 'stale': {
        // Concurrent write; the append's compare-and-set lost. Retry ONCE.
        if (retriedStale.current) {
          showToast(error?.detail ?? 'This RFQ changed while we were adding that part.', 'error')
          onChanged()
          return
        }
        retriedStale.current = true
        await append(sourceable)
        return
      }
      default:
        // Includes 403 — a capability gate this predicate should already have
        // closed, so reaching here is a bug, not a user path. Surfaced
        // honestly rather than swallowed.
        showToast(error?.detail ?? 'Could not add that part. Please try again.', 'error')
        onChanged()
        return
    }
  }

  /** Open/close the Add-parts dialog. Both reset the `stale` retry budget
   * (Gate-5 L1) — a dialog session is the natural boundary for it. */
  function setAddPartsDialog(open: boolean) {
    retriedStale.current = false
    setAddPartsOpen(open)
  }

  async function handleAdd(sourceable: Parameters<typeof append>[0]) {
    if (addingRef.current) return
    addingRef.current = true
    // One retry per attempt (see `retriedStale`), so `append`'s recursive retry
    // still gets exactly one shot and nothing inherits a spent budget.
    retriedStale.current = false
    setAdding(true)
    try {
      await append(sourceable)
    } finally {
      addingRef.current = false
      setAdding(false)
    }
  }

  // UID-294 §10 (verify/align, `Fictiv Platform.dc.html:3715-3723`) — the
  // same "no fabrication" ETA basis `RfqCard.tsx`'s in-progress stepper chip
  // uses (`submittedAt`, falling back to `createdAt` only as a degraded
  // anchor for a legacy row with neither — see that chip's doc comment for
  // the full rationale); `null` (neither date present) renders no ETA
  // clause at all, same as `RfqEtaChip`.
  const eta = etaForRfq(rfq.submittedAt ?? rfq.createdAt ?? '')

  return (
    <RailCard>
      <ul className="rfq-detail-summary-parts-list">
        {/* UID-294 §10 (verify/align) — each row is a name/qty PAIR (was
         * qty-only), matching the design's `{{ p.name }}`/`{{ p.qtyRight }}`
         * per-part list. The `RfqLineView` cast mirrors `RfqCard.tsx`'s own
         * (see that file's doc comment): plain property access on
         * `Rfq['lines']` doesn't expose the additive `part` field. */}
        {(rfq.lines as readonly RfqLineView[]).map((line, index) => (
          <li key={`${line.sourceable.id}-${index}`}>
            <span
              className="rfq-detail-summary-part-name"
              title={line.part?.name ?? displayNameFor(line.sourceable)}
            >
              {line.part?.name ?? displayNameFor(line.sourceable)}
            </span>
            <span className="rfq-detail-summary-part-qty">{formatQuantity(line.quantity)}</span>
          </li>
        ))}
      </ul>
      {/* UID-265c (ADR-0093 D5) — `submittedAt`, NOT `createdAt`. Since the
       * ADR-0082 eager-draft change `createdAt` is the moment the FIRST PART
       * was added at compose time, potentially days before submission, so
       * "Submitted <createdAt>" was displaying a wrong date.
       *
       * There is deliberately NO `createdAt` fallback: when `submittedAt` is
       * null (a legacy/never-captured row) this line is OMITTED entirely.
       * Falling back would reinstate the exact lie this fix removes, and would
       * remove any incentive to populate the column. A pre-existing demo row
       * loses a line that was showing a wrong date — the correct direction of
       * loss. */}
      {rfq.submittedAt && (
        <p className="rfq-detail-summary-submitted">
          Submitted <time dateTime={rfq.submittedAt}>{rfq.submittedAt.slice(0, 10)}</time>
        </p>
      )}
      {/* UID-294 §10 (verify/align, `Fictiv Platform.dc.html:3715-3723`) —
       * the full copy includes the "Pricing will appear…estimated return"
       * clause when an ETA is derivable; degrades to the first sentence
       * alone (unchanged from before this ticket) when it is not. */}
      <p>
        Our sourcing team is reviewing your files.
        {eta && (
          <>
            {' '}
            Pricing will appear above, in this summary, once quoted — estimated return <b>{eta}</b>.
          </>
        )}
      </p>
      {/* ADR-0093 D6 (2) — ONE `aria-live="polite"` container slot both
       * controls render into, so the APPEARANCE of "Edit RFQ" (which can
       * happen while the user is on the page, on a refetch that observes
       * `requested → quoting`) is announced once. Deliberately NOT on the
       * button itself, which would re-announce on every re-render of its own
       * subtree. There is no focus move and no `autoFocus` — an unrequested
       * focus jump seconds after load is exactly the WCAG 3.2.1/3.2.2 failure
       * those criteria exist for (D6 (3)). */}
      <div className="rfq-active-control-slot" aria-live="polite">
        {control === 'edit-rfq' && (
          /* ADR-0093 D6 (1) — a REAL `<button>` that APPEARS. Never a
           * `disabled → enabled` flip: a disabled control is outside the tab
           * order, so AT never had it to begin with and the "enable" is
           * invisible. Icon PRECEDES the label, per design L3725. */
          <button
            type="button"
            className="rfq-edit-rfq-btn"
            onClick={() => setAddPartsDialog(true)}
          >
            <span className="rfq-edit-rfq-icon">
              <Icon name="edit" />
            </span>
            Edit RFQ
          </button>
        )}
        {control === 'requested-indicator' && <RequestedIndicator />}
      </div>
      <AddPartsToRfqModal
        open={addPartsOpen}
        search={searchAddPartsCandidates}
        alreadyOnRfq={alreadyOnRfq}
        adding={adding}
        onClose={() => setAddPartsDialog(false)}
        onAdd={(sourceable) => void handleAdd(sourceable)}
        onUploadNewFiles={() => navigate('/uploads')}
      />
    </RailCard>
  )
}

/** ADR-0094 D2/D6 — shipped in `c3deb276`, unamended by ADR-0093. Extracted
 * only so the swap in `ActiveSummaryRail` above reads as two mutually
 * exclusive branches of one slot. */
function RequestedIndicator() {
  return (
    <>
      {/* §3.2 a11y: a plain, non-focusable `<span>` — never a real enabled
       * control that does nothing on activation. Meaning is carried by the
       * TEXT, never by color alone (WCAG 1.4.1), mirroring `RfqEtaChip`'s own
       * documented convention.
       *
       * UID-265 (ADR-0094 D6) — the design export (`Fictiv Platform.dc.html`
       * L3724) renders this as `<button disabled>`. We replicate its
       * GEOMETRY via CSS but deliberately NOT its SEMANTICS: the `<button>`
       * there is a prototype-convenience artifact (it lets the Requested and
       * Edit RFQ controls share one style block), not a semantic intent. A
       * disabled button announces "here is an action" where none exists, is
       * outside the tab order, and is poorly enumerated by AT. So this stays
       * a plain non-focusable `<span>`.
       *
       * ADR-0094 D2 — the decorative checkmark is REPLACED by the design's
       * pulsing dot: a checkmark asserts completion, the design asserts
       * in-flight activity, and the RFQ is demonstrably not complete here.
       * The dot is `aria-hidden` and purely decorative (ADR-0094 D5). */}
      <span className="rfq-requested-control">
        <span className="rfq-requested-dot" aria-hidden="true" />
        Requested
      </span>
    </>
  )
}

/** §3.3 — Expired. */
function ExpiredSummaryRail({
  rfq,
  onChanged,
}: {
  readonly rfq: Rfq
  readonly onChanged: (fresh?: Rfq) => void
}) {
  const { showToast } = useToast()
  const [resubmitting, setResubmitting] = useState(false)

  // UID-264 (ADR-0056/0085): resubmit flips THIS expired RFQ in place —
  // `POST /rfq/{rfqId}/resubmit` reactivates the SAME RFQ id (expired →
  // quoting, reusing the same Quote row reset server-side; `rfq.quote.rfq_id`
  // is UNIQUE and DELETE is forbidden at the schema/grant layer), reversing the
  // prior resubmit-as-a-new-RFQ flow that created a SECOND RFQ and stranded the
  // user on the old one. The compare-and-set transition and the
  // 409-on-non-expired guard are enforced server-side (a double-click cannot
  // double-transition), so the only client concern is the in-flight guard.
  //
  // UID-265d (ADR-0093 D4, ADR-0094 F2) — the ONE refinement this branch makes
  // on top of UID-264: resubmit answers `200` + the whole refreshed `RfqView`
  // for the SAME id, so that response IS this page's fresh state. Hydrate
  // `onChanged` from it (D4's widened optional argument) instead of paying for a
  // second `GET`. The rendered consequence is unchanged from UID-264 — the
  // in-progress stepper plus the fresh submitted date/ETA — and because resubmit
  // lands in `quoting`, the detail re-renders as `ActiveSummaryRail` with Edit
  // RFQ showing immediately and no Requested pill: the intended consequence of
  // the status-derived predicate (ADR-0093 D6 (b)), not a bug. Tab counts update
  // on the list's next load, since `tabForStatus('quoting')` is 'requested'.
  async function handleResubmit() {
    if (resubmitting) return
    setResubmitting(true)
    try {
      const { data, error } = await apiClient.POST('/rfq/{rfqId}/resubmit', {
        params: { path: { rfqId: rfq.id } },
      })
      if (!data) {
        showToast(error?.detail ?? 'Could not resubmit this RFQ. Try again.', 'error')
        return
      }
      showToast('Resubmitted — your RFQ is back in review for fresh pricing.', 'success')
      // Same id, so the response IS this page's refreshed RfqView (ADR-0093 D4).
      onChanged(data)
    } finally {
      setResubmitting(false)
    }
  }

  // UID-327 AC4 (ADR-0112) — the expired rail's Parts count, derived from
  // `rfq.lines` directly (NOT `quote.lines.filter(isQuoteLineAddable)`, the
  // way `ReturnedSummaryRail`'s Parts row at `:375-379` does): this rail's
  // props are `{ rfq, onChanged }` only — it fetches no quote, and the
  // parent (`RfqDetailSummaryRail` above) passes none — so there is no
  // quote-derived line set to count here. `rfq.lines` is exactly the set
  // `ActiveSummaryRail` already renders as its own parts list (`:748`), so
  // this reuses the same source the sibling non-quoted rail relies on rather
  // than inventing a second one.
  const partsCount = rfq.lines.length

  return (
    <RailCard>
      <dl className="rfq-detail-summary-fields">
        {/* Design source (`Fictiv Platform.dc.html:3700`): the "Parts"
         * row is rendered UNCONDITIONALLY, once, ahead of the
         * isReturned/isActive/isExpired branches — it is not part of the
         * isExpired-only block. Order preserved here: Parts, then the
         * isExpired block's own Quoted row (`dc.html:3727-3732`). ADR-0115
         * F9 — a BARE integer (`{{ r.partCount }}`, no runtime
         * pluralization), the same fix applied consistently to
         * `ReturnedSummaryRail`'s own Parts row above, so the one
         * design-literal field doesn't drift between the two rails. */}
        <SummaryField term="Parts">{partsCount}</SummaryField>
        {/* UID-327 AC4 (ADR-0112) — CORRECTED field: this row previously read
         * `rfq.expiresAt` (the quote's validity-window END — an expiry date,
         * per `apps/api/contracts/api-specs/openapi/components/schemas.yaml`'s
         * "Expiry / archival, an independent control from any pricing lock"
         * doc string) as a documented "interim proxy" for a quoted date. That
         * was always the wrong field: `RfqView.quotedAt` is the real,
         * purpose-built "when this RFQ was quoted" timestamp, already
         * consumed by `RfqListRow.tsx`'s `quotedDate` and by
         * `rfqSubmittedQuotedLabel` (`domain.ts`) for the same "Quoted
         * <date>" copy. This row now sources from `quotedAt` and drops the
         * interim-proxy disclosure — there is nothing left to disclose.
         *
         * The row itself stays UNCONDITIONAL (never omitted) because
         * `quotedAt` is ALSO nullable — "nullable until the quote returns"
         * per that same `domain.ts` doc comment — and at least one real
         * seeded RFQ has it null. The AC4 "no vanished row" requirement is
         * met by always rendering the row, with an honest fallback when the
         * date is absent, rather than by the field always being populated.
         * Fallback string matches this file's own established convention
         * for an absent value (`ReturnedSummaryRail`'s Lead time/Quantity/
         * Total rows all fall back to the bare em dash `'—'`, `:296,:433,
         * :454,:494,:501` — not `RfqCard.tsx`/`RfqListRow.tsx`'s unrelated
         * "Ship by N/A" convention, which is that file's own established
         * idiom for a different field). */}
        <SummaryField term="Quoted">
          {rfq.quotedAt ? <time dateTime={rfq.quotedAt}>{rfq.quotedAt.slice(0, 10)}</time> : '—'}
        </SummaryField>
      </dl>
      {/* Design source (`Fictiv Platform.dc.html:3729`): a plain divider rule
       * between the Quoted row and the expiry copy. No existing divider
       * class in this rail was reusable — checked first, per the ticket's
       * instruction — so `.rfq-detail-summary-divider` (rfq.css) is a
       * genuinely new, minimal style. This row carries no visible label, so
       * an empty `<dt>`/`<dd>` pair (as opposed to `SummaryField`) would
       * announce nothing useful to a screen reader; a bare decorative `<hr>`
       * communicates the same visual break without that noise. No
       * `aria-hidden` — biome's `useAriaPropsForRole`/focusable-element lint
       * flags it here, and `<hr>`'s native `separator` role is harmless for
       * AT to announce (unlike the decorative icons elsewhere in this file
       * that DO carry `aria-hidden`). */}
      <hr className="rfq-detail-summary-divider" />
      {/* UID-264 (Fictiv Platform.dc.html:3730): the design's exact expired-
       * card body copy. */}
      <p>This quote expired. Resubmit the request to get fresh pricing.</p>
      <Button
        variant="primary"
        size="lg"
        className="rfq-resubmit-btn"
        onClick={handleResubmit}
        loading={resubmitting}
      >
        Resubmit RFQ
      </Button>
    </RailCard>
  )
}
